import { CommonModule, NgClass } from '@angular/common';
import { Component, computed, input, signal, Signal, WritableSignal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import isFunction from 'lodash-es/isFunction';
import { Observable, Subscription } from 'rxjs';
import { DataGridComponentInjectorDirective } from 'shared/components/data-grid/data-grid-component-injector.directive';
import { DataGridSorterPipe } from 'shared/pipes/data-grid-sorter.pipe';
import { DataGridColumnDefinition } from '../../interfaces/data-grid.interface';

export interface RowStyleRules {
    [cssClassName: string]: (params: { [key: string]: any }) => boolean;
}

interface SortingData {
    column: number;
    direction: -1 | 0 | 1;
    dataPreprocessor?: Function | undefined;
}

@Component({
    selector: 'app-data-grid',
    templateUrl: './data-grid.component.html',
    styleUrls: ['./data-grid.component.scss'],
    imports: [NgClass, TranslateModule, DataGridSorterPipe, DataGridComponentInjectorDirective, CommonModule],
})
export class DataGridComponent {
    columns = input.required<DataGridColumnDefinition[]>();
    data = input.required<{ [key: string]: any }[]>();
    rowStyleRules = input<RowStyleRules>({});
    forceRefresh = input<number>();

    private subscriptions: { [key: string]: Subscription } = {};

    public sorting: WritableSignal<SortingData> = signal({
        column: -1, // Default is no sort
        direction: 0, // Default is no direction
        dataPreprocessor: undefined,
    });

    public processedColumns: Signal<DataGridColumnDefinition[]>;
    public itemStylesMap: Signal<WeakMap<{ [key: string]: any }, string | null>>;

    constructor() {
        this.processedColumns = computed(() => {
            this.forceRefresh();
            const currentColumns = this.columns();
            if (!currentColumns || currentColumns.length === 0) {
                return [];
            }
            const columnsWithoutHeaderLabels = currentColumns.filter((column) => !column.headerLabel).length;
            return currentColumns.map((column) => {
                const widthPercentage =
                    currentColumns.length - columnsWithoutHeaderLabels > 0 ? 100 / (currentColumns.length - columnsWithoutHeaderLabels) : 0;

                let cellClasses = '';
                if (column.dataKey === 'widgetComparisonState') {
                    cellClasses += 'changeTrackingRow ';
                }
                if (column.cellClass) {
                    cellClasses += column.cellClass;
                }

                return {
                    ...column,
                    // The default table cell width calculation generally works fine, but
                    // the cells which have a link have issues with the .cellWarpper break-work style.
                    // The ones without a header only have buttons, so they can be excluded.
                    width:
                        column.headerLabel &&
                        (!!column.customCellRendererData?.linkAction || !!column.customCellRendererData?.getAction) &&
                        widthPercentage > 0
                            ? widthPercentage + '%'
                            : 'auto',
                    computedCellClass: cellClasses.trim() || null,
                };
            });
        });

        this.itemStylesMap = computed(() => {
            const currentData = this.data();
            const rules = this.rowStyleRules();
            const map = new WeakMap<{ [key: string]: any }, string | null>();

            currentData.forEach((item) => {
                let classes = '';
                Object.keys(rules).forEach((ruleKey) => {
                    if (rules[ruleKey](item)) {
                        classes += `${ruleKey} `;
                    }
                });
                const trimmedClasses = classes.trim();
                map.set(item, trimmedClasses !== '' ? trimmedClasses : null);
            });
            return map;
        });
    }

    public changeSort(columnIndex: number): void {
        const currentColumns = this.processedColumns();
        if (!currentColumns[columnIndex]?.sortable || !currentColumns[columnIndex]?.dataKey) {
            return;
        }

        this.sorting.update((currentSorting) => {
            const newDirection = currentSorting.column === columnIndex ? ((currentSorting.direction * -1) as -1 | 0 | 1) : 1;
            const newSortingState: SortingData = {
                column: columnIndex,
                direction: newDirection === 0 && currentSorting.direction !== 0 ? ((currentSorting.direction * -1) as -1) : newDirection, // Ensure direction flips if it becomes 0
                dataPreprocessor: undefined,
            };

            if (currentColumns[columnIndex]?.sortDataPreprocessor) {
                const sortDataPreprocessor = currentColumns[columnIndex].sortDataPreprocessor;

                if (sortDataPreprocessor instanceof Observable) {
                    this.subscriptions.columnSortFunction?.unsubscribe();
                    this.subscriptions.columnSortFunction = sortDataPreprocessor.subscribe((sortDataPreprocessorFunction) => {
                        this.sorting.update((s) => ({ ...s, dataPreprocessor: sortDataPreprocessorFunction }));
                    });
                } else if (isFunction(sortDataPreprocessor)) {
                    newSortingState.dataPreprocessor = sortDataPreprocessor as Function;
                }
            }
            return newSortingState;
        });
    }
}
